import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { SimulationObject } from 'src/app/core/interfaces/simulation-object';
import GraphicProperties from 'src/app/core/models/3D/graphic-properties/graphic-properties.model';
import { Clone } from 'src/app/shared/utils/clone';

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  typeById: Map<number, string>;
  objectById: Map<number, SimulationObject>;
  graphicPropById: Map<number, GraphicProperties>;
  titles: string[];
  ignoredTitlesFromTypes = [CONSTANTS.NODE];

  constructor() {
    this.resetMap();
  }

  resetMap(): void {
    this.typeById = new Map();
    this.objectById = new Map();
    this.graphicPropById = new Map();
    this.titles = [];
  }

  saveTypeById(id: number, type: string): void {
    this.typeById.set(id, type);
  }

  saveObjectById(id: number, object: any): void {
    const objCopy = Clone(object);
    this.objectById.set(id, objCopy);
  }

  saveInAllMaps(type: string, object: any): void {
    this.saveTypeById(object.internalId, type);
    this.saveObjectById(object.internalId, object);

    if (object.title && !this.ignoredTitlesFromTypes.includes(type)) {
      this.titles.push(object.title);
    }
  }

  titleExists(title: string) {
    return this.titles.includes(title);
  }

  deleteFromMaps(id: number): void {
    this.deleteTitleById(id);
    this.typeById.delete(id);
    this.objectById.delete(id);
    this.graphicPropById.delete(id);
  }

  deleteTitleById(id: number): void {
    const title = this.getObjectById(id)?.title;
    const titleIndex = this.titles.indexOf(title);
    this.titles.splice(titleIndex, 1);
  }

  getObjectById(id: number): any {
    return this.objectById.get(id);
  }

  getTypeById(id: number): string {
    return this.typeById.get(id);
  }

  updateObject(object: SimulationObject, type: string = null): void {
    const { internalId, title } = object;
    const prevObjTitle = this.objectById.get(internalId)?.title;

    this.saveObjectById(internalId, object);
    if (type !== null) {
      this.saveTypeById(internalId, type);
    }

    if (prevObjTitle !== title) {
      const indexOfTitle = this.titles.indexOf(prevObjTitle);
      this.titles[indexOfTitle] = title;
    }
  }

  isDuplicatedTitle(title: string): boolean {
    return this.titles.filter((currTitle) => currTitle === title).length > 1;
  }

  get hasGraphicPropsMap(): boolean {
    return !!this.graphicPropById.size;
  }

  setGraphicPropById(id: number, prop: GraphicProperties): void {
    this.graphicPropById.set(id, prop);
  }

  getGraphicPropById(id: number): GraphicProperties {
    return this.graphicPropById.get(id);
  }
}
